import {
  claimAttachmentUploadFileSchema,
  type ClaimAttachmentSummary,
} from '@billing/validation/attachment';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  FileButton,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconEye,
  IconPaperclip,
  IconUpload,
} from '@tabler/icons-react';
import {
  claimAttachmentQueryKeys,
  completeClaimAttachment,
  getAttachmentDownloadURL,
  prepareClaimAttachment,
  useGetClaimAttachments,
} from 'api/resource/claimAttachment';
import { useDownloadFromSignedURL } from 'hooks/useDownloadFromSignedURL';
import { useEffect, useRef, useState } from 'react';
import { formatDateTime } from 'utils/dateUtils';
import { getFileAppearance, getFilenameExtension } from 'utils/fileUtils';
import { uploadFileToSignedURL } from 'utils/fileUpload';
import { ClaimAttachmentViewer } from './ClaimAttachmentViewer';

const AttachmentCard = ({
  attachment,
  onView,
  onDownloadError,
  timezone,
}: {
  attachment: ClaimAttachmentSummary;
  onView: () => void;
  onDownloadError: () => void;
  timezone?: string;
}) => {
  const { translate } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFilenameTruncated, setIsFilenameTruncated] = useState(false);
  const filenameRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    const filenameElement = filenameRef.current;
    if (!filenameElement) return;

    const updateTruncation = () => {
      setIsFilenameTruncated(
        filenameElement.scrollWidth > filenameElement.clientWidth
      );
    };
    updateTruncation();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateTruncation);
    observer.observe(filenameElement);
    return () => observer.disconnect();
  }, [attachment.originalFilename]);
  const download = useDownloadFromSignedURL({
    getSignedURL: () => getAttachmentDownloadURL(attachment.id),
    filename: attachment.originalFilename,
    setLoading: setIsDownloading,
    onError: onDownloadError,
  });
  const appearance = getFileAppearance(attachment.originalFilename);
  const getProvenance = (): string | null => {
    if (attachment.createdByName) {
      return `${translate(
        'pages:claims.attachments.uploadedBy',
        'Uploaded by'
      )} ${attachment.createdByName}`;
    }
    if (attachment.createdBy === null) {
      return translate(
        'pages:claims.attachments.systemGenerated',
        'System generated'
      );
    }
    return null;
  };
  const provenance = getProvenance();
  const status = attachment.claimQueryStatus;
  const statusLabel = status
    ? translate(
        `pages:claims.supportingDocument.statuses.${status.toLowerCase()}`,
        status
      )
    : null;

  return (
    <Paper
      role="listitem"
      withBorder
      radius="md"
      shadow="xs"
      p="sm"
      mih={88}
      w="100%"
      bg={
        status === 'FAILED'
          ? 'light-dark(var(--mantine-color-red-0), rgba(250, 82, 82, 0.08))'
          : 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))'
      }
      style={
        status === 'FAILED'
          ? {
              borderColor:
                'light-dark(var(--mantine-color-red-3), var(--mantine-color-red-8))',
            }
          : undefined
      }
    >
      <Group wrap="nowrap" gap="sm" mih={62} style={{ minWidth: 0 }}>
        <ThemeIcon
          size={48}
          radius={6}
          variant="light"
          color={appearance.color}
        >
          {appearance.icon}
        </ThemeIcon>
        <Stack gap={1} style={{ minWidth: 0, flex: 1 }}>
          <Tooltip
            label={attachment.originalFilename}
            disabled={!isFilenameTruncated}
            events={{ hover: true, focus: true, touch: false }}
            withArrow
          >
            <Text
              ref={filenameRef}
              size="sm"
              fw={600}
              truncate
              tabIndex={isFilenameTruncated ? 0 : undefined}
            >
              {attachment.originalFilename}
            </Text>
          </Tooltip>
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              {getFilenameExtension(attachment.originalFilename).toUpperCase()}
            </Text>
            {statusLabel && (
              <Badge
                size="xs"
                variant="light"
                color={status === 'FAILED' ? 'red' : 'blue'}
              >
                {statusLabel}
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            {[provenance, formatDateTime(attachment.createdAt, timezone)]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        </Stack>
        <Group gap={4} wrap="nowrap">
          <Button
            type="button"
            size="xs"
            variant="subtle"
            leftSection={<IconEye size={15} />}
            onClick={onView}
          >
            {translate('pages:claims.attachments.view', 'View')}
          </Button>
          <Tooltip
            label={translate('pages:claims.attachments.download', 'Download')}
          >
            <ActionIcon
              type="button"
              variant="subtle"
              color="gray"
              loading={isDownloading}
              aria-label={translate(
                'pages:claims.attachments.download',
                'Download'
              )}
              onClick={() => void download()}
            >
              <IconDownload size={17} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Paper>
  );
};

export const ClaimAttachmentsCard = ({
  claimID,
  canUpload,
  timezone,
}: {
  claimID: string;
  canUpload: boolean;
  timezone?: string;
}) => {
  const { translate } = useTranslation();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttachment, setSelectedAttachment] =
    useState<ClaimAttachmentSummary | null>(null);
  const attachments = useGetClaimAttachments({ claimID, enabled: true });

  const upload = async (file: File | null): Promise<void> => {
    if (!file) return;
    setExpanded(true);
    setError(null);
    const metadata = claimAttachmentUploadFileSchema.safeParse({
      originalFilename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    });
    if (!metadata.success) {
      setError(
        translate(
          'pages:claims.attachments.invalidUpload',
          'Choose a supported file smaller than 10 MB.'
        )
      );
      return;
    }
    setIsUploading(true);
    try {
      const target = await prepareClaimAttachment({
        claimID,
        file: metadata.data,
      });
      await uploadFileToSignedURL({
        signedURL: target.uploadURL,
        data: file,
        contentType: metadata.data.contentType,
      });
      await completeClaimAttachment({
        claimID,
        attachment: { ...metadata.data, id: target.id },
      });
      await queryClient.invalidateQueries({
        queryKey: claimAttachmentQueryKeys.byClaim(claimID),
      });
    } catch {
      setError(
        translate(
          'pages:claims.attachments.uploadError',
          'The attachment could not be uploaded.'
        )
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card
      withBorder
      radius="sm"
      padding="lg"
      shadow="xs"
      mt="md"
      w="100%"
      bg="light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))"
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs">
          <IconPaperclip size={20} />
          <Text fw={600}>
            {translate('pages:claims.attachments.cardTitle', 'Attachments')}
          </Text>
          <Badge variant="light" color="gray" size="sm">
            {attachments.data?.length ?? 0}
          </Badge>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {canUpload && (
            <FileButton onChange={(file) => void upload(file)}>
              {(props) => (
                <Button
                  {...props}
                  type="button"
                  size="sm"
                  variant="outline"
                  color="gray"
                  c="var(--mantine-color-text)"
                  leftSection={<IconUpload size={15} />}
                  loading={isUploading}
                >
                  {translate('pages:claims.attachments.upload', 'Upload')}
                </Button>
              )}
            </FileButton>
          )}
          <ActionIcon
            type="button"
            size="input-sm"
            variant="subtle"
            color="gray"
            radius="sm"
            aria-label={translate(
              expanded
                ? 'pages:claims.attachments.collapse'
                : 'pages:claims.attachments.expand',
              expanded ? 'Collapse attachments' : 'Expand attachments'
            )}
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? (
              <IconChevronUp size={18} />
            ) : (
              <IconChevronDown size={18} />
            )}
          </ActionIcon>
        </Group>
      </Group>
      {expanded && (
        <Stack gap="xs" mt="md">
          {attachments.isLoading && (
            <Group justify="center" mih={72}>
              <Loader size="sm" />
            </Group>
          )}
          {attachments.isError && (
            <Alert color="red">
              {translate(
                'pages:claims.attachments.loadError',
                'Claim attachments could not be loaded.'
              )}
            </Alert>
          )}
          {!attachments.isLoading &&
            !attachments.isError &&
            !attachments.data?.length && (
              <Text c="dimmed" size="sm">
                {translate(
                  'pages:claims.attachments.cardEmpty',
                  'No attachments have been added to this claim.'
                )}
              </Text>
            )}
          {attachments.data && attachments.data.length > 0 && (
            <Box
              role="list"
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns:
                  'repeat(auto-fill, minmax(min(100%, max(360px, calc(33.333% - 8px))), 1fr))',
              }}
            >
              {attachments.data.map((attachment) => (
                <AttachmentCard
                  key={attachment.id}
                  attachment={attachment}
                  timezone={timezone}
                  onView={() => setSelectedAttachment(attachment)}
                  onDownloadError={() =>
                    setError(
                      translate(
                        'pages:claims.attachments.downloadError',
                        'The attachment could not be downloaded.'
                      )
                    )
                  }
                />
              ))}
            </Box>
          )}
          {error && <Alert color="red">{error}</Alert>}
        </Stack>
      )}
      {selectedAttachment?.claimID === claimID && (
        <ClaimAttachmentViewer
          key={selectedAttachment.id}
          attachment={selectedAttachment}
          onClose={() => setSelectedAttachment(null)}
        />
      )}
    </Card>
  );
};
